import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth, anonymize } from "../App";
import { Calendar, ChevronDown, ChevronRight, Search, Loader2, Printer, UserCheck, Mail, Copy, FileText, UserCog } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import BenchJudgePicker from "../components/BenchJudgePicker";
import CompositionPicker from "../components/CompositionPicker";
import { ReassignDialog } from "../components/ReassignDialog";
import { NeedsReassignmentBadge, singleCaseReassignTarget } from "../components/NeedsReassignmentBadge";
import { JudgeOptionLabel } from "../components/JudgeOptionLabel";
import { SynthesisActsButton } from "../components/SynthesisActsDialog";
import { RoleScopeBanner, isScopedRole, isPresidentRole } from "../components/RoleScopeBanner";
import { BranchFilterSelect } from "../components/Workspace";
import { readWorkspace } from "../utils/workspace";
import { DocketButton, docketKey } from "../components/DocketButton";
import { DateInput } from "../components/ui/date-input";

const Hearings = () => {
  const { user } = useAuth();
  const privacy = user?.privacy_mode || false;
  const p = (text) => privacy ? anonymize(text) : text;
  const [allPinakia, setAllPinakia] = useState([]);
  const [docketMap, setDocketMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(() => new Date().toISOString().split("T")[0]);
  const [dateTo, setDateTo] = useState(() => {
    const d = new Date(); d.setFullYear(d.getFullYear() + 1);
    return d.toISOString().split("T")[0];
  });
  const userCourtTypes = user?.court_types || ["Μονομελές", "Πολυμελές"];
  const [courtType, setCourtType] = useState(() => userCourtTypes.length === 1 ? userCourtTypes[0] : (readWorkspace(user)?.court_type || ""));
  // iter109: φίλτρο Καταστήματος (προεπιλογή από τον χώρο εργασίας) — δεν φορτώνουμε όλα τα πινάκια
  const [branchFilter, setBranchFilter] = useState(() => readWorkspace(user)?.branch || "");
  const [pinakioSearch, setPinakioSearch] = useState("");
  const [totalCases, setTotalCases] = useState(0);
  const [expandedPinakia, setExpandedPinakia] = useState({});
  const [expandedDates, setExpandedDates] = useState({});
  const [assignJudgeFor, setAssignJudgeFor] = useState(null); // {pinakioLabel, date}
  const [reassignTarget, setReassignTarget] = useState(null); // {pinakioLabel, hearingDate, courtType}
  const [judgeName, setJudgeName] = useState("");
  const [judgeSearch, setJudgeSearch] = useState("");
  const [allUsers, setAllUsers] = useState([]);
  const [pretrialJudgesByCT, setPretrialJudgesByCT] = useState({ "Μονομελές": [], "Πολυμελές": [] });
  const [assigning, setAssigning] = useState(false);
  const [emailDialog, setEmailDialog] = useState(null); // {subject, body}
  const [showEmailConfirm, setShowEmailConfirm] = useState(null); // pending email data
  const [hearingsView, setHearingsView] = useState("pinakia"); // "pinakia" or "slots"
  const [slotsDateFrom, setSlotsDateFrom] = useState(() => new Date().toISOString().split("T")[0]);
  const [slotsDateTo, setSlotsDateTo] = useState("");
  const [slotsSubject, setSlotsSubject] = useState("");
  const [slotsCourtType, setSlotsCourtType] = useState("");
  const [slotsData, setSlotsData] = useState(null);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsPinakioSearch, setSlotsPinakioSearch] = useState("");
  const [slotsExporting, setSlotsExporting] = useState(false);
  const [subjectOptions, setSubjectOptions] = useState([]);
  const [courtTypeOptions, setCourtTypeOptions] = useState([]);
  const [exportJob, setExportJob] = useState(null); // {job_id, status, progress, message, filename, error, format}

  useEffect(() => {
    // Load users for judge dropdown
    axios.get(`${API}/users`).then(res => setAllUsers(res.data || [])).catch(() => {});
    // Φόρτωση Δικαστών Προδικαστικού Ελέγχου ανά τύπο δικαστηρίου για dropdown
    ["Μονομελές", "Πολυμελές"].forEach(ct => {
      axios.get(`${API}/pretrial-judges-list?court_type=${encodeURIComponent(ct)}`)
        .then(res => setPretrialJudgesByCT(prev => ({ ...prev, [ct]: res.data?.judges || [] })))
        .catch(() => {});
    });
    // Load distinct subjects + counts for autocomplete
    axios.get(`${API}/cases/subjects-with-counts`)
      .then(res => setSubjectOptions(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
    // Load distinct court types from DB
    axios.get(`${API}/lookup/court-types`)
      .then(res => setCourtTypeOptions(Array.isArray(res.data) ? res.data : []))
      .catch(() => setCourtTypeOptions(["Μονομελές", "Πολυμελές"]));
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchHearings(); }, [dateFrom, dateTo, courtType, branchFilter]);

  const fetchHearings = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      if (courtType && courtType !== "all") params.append("court_type", courtType);
      if (branchFilter) params.append("branch", branchFilter);
      const r = await axios.get(`${API}/court-rolls?${params.toString()}`, { timeout: 120000 });
      const pinakia = r.data.pinakia || [];
      setAllPinakia(pinakia);
      axios.get(`${API}/solon/dockets/lookup`, { params: { date_from: dateFrom || undefined, date_to: dateTo || undefined } })
        .then(res => { const m = {}; (res.data.items || []).forEach(it => { m[docketKey(it.pinakio_name, it.hearing_date)] = it; }); setDocketMap(m); })
        .catch(() => {});
      setTotalCases(r.data.total_cases || 0);
      const ep = {};
      const ed = {};
      pinakia.forEach(p => {
        ep[p.label] = true;
        if (p.dates?.length > 0) {
          const today = new Date().toISOString().split("T")[0];
          const upcoming = p.dates.find(d => d.date >= today);
          ed[`${p.label}|${(upcoming || p.dates[0]).date}`] = true;
        }
      });
      setExpandedPinakia(ep);
      setExpandedDates(ed);
    } catch {
      toast.error("Σφάλμα φόρτωσης πινακίων");
    } finally {
      setLoading(false);
    }
  };

  const handleAssignJudge = async (pinakioLabel, date) => {
    if (!judgeName.trim()) { toast.error("Επιλέξτε δικαστή"); return; }
    try {
      setAssigning(true);
      const pinakio = allPinakia.find(p => p.label === pinakioLabel);
      const dateGroup = pinakio?.dates?.find(d => d.date === date);
      const ct = dateGroup?.cases?.[0]?.court_type || "Μονομελές";
      
      const res = await axios.post(`${API}/hearings/assign-judge-to-pinakio`, {
        hearing_date: date,
        court_type: ct,
        judge_name: judgeName.trim()
      });
      toast.success(res.data.message || "Ο δικαστής ορίστηκε");
      
      // Ask if user wants to send email
      if (res.data.email_body) {
        setShowEmailConfirm({ subject: res.data.email_subject, body: res.data.email_body });
      }
      
      setAssignJudgeFor(null);
      setJudgeName("");
      setJudgeSearch("");
      fetchHearings();
    } catch { toast.error("Σφάλμα ανάθεσης δικαστή"); }
    finally { setAssigning(false); }
  };

  const handleCopyEmail = () => {
    if (emailDialog) {
      navigator.clipboard.writeText(`Θέμα: ${emailDialog.subject}\n\n${emailDialog.body}`);
      toast.success("Το email αντιγράφηκε");
    }
  };

  const handleSendEmail = () => {
    if (emailDialog) {
      const mailto = `mailto:?subject=${encodeURIComponent(emailDialog.subject)}&body=${encodeURIComponent(emailDialog.body)}`;
      window.open(mailto, "_blank");
    }
  };

  const filteredUsers = allUsers.filter(u => {
    const search = judgeSearch.toLowerCase();
    return (u.full_name || "").toLowerCase().includes(search) || (u.username || "").toLowerCase().includes(search);
  });

  const displayPinakia = pinakioSearch.trim()
    ? allPinakia.filter(p =>
        p.label.toLowerCase().includes(pinakioSearch.trim().toLowerCase()) ||
        p.pinakio_code.toLowerCase().includes(pinakioSearch.trim().toLowerCase())
      )
    : allPinakia;

  const displayCaseCount = displayPinakia.reduce((s, p) => s + p.total_cases, 0);

  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const d = new Date(dateStr + "T00:00:00");
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const diff = Math.round((d - today) / 86400000);
      const formatted = d.toLocaleDateString("el-GR", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
      if (diff === 0) return `${formatted} (Σήμερα)`;
      if (diff === 1) return `${formatted} (Αύριο)`;
      if (diff > 0 && diff <= 7) return `${formatted} (σε ${diff} ημ.)`;
      return formatted;
    } catch { return dateStr; }
  };

  const isDatePast = (dateStr) => {
    try { return new Date(dateStr + "T00:00:00") < new Date(new Date().toDateString()); }
    catch { return false; }
  };

  const setToday = () => { const t = new Date().toISOString().split("T")[0]; setDateFrom(t); setDateTo(t); };
  const setThisWeek = () => {
    const now = new Date(); const dow = now.getDay();
    const mon = new Date(now); mon.setDate(now.getDate() - (dow === 0 ? 6 : dow - 1));
    const fri = new Date(mon); fri.setDate(mon.getDate() + 4);
    setDateFrom(mon.toISOString().split("T")[0]); setDateTo(fri.toISOString().split("T")[0]);
  };
  const setThisMonth = () => {
    const now = new Date();
    setDateFrom(new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split("T")[0]);
    setDateTo(new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split("T")[0]);
  };
  const setUpcoming = () => { setDateFrom(new Date().toISOString().split("T")[0]); setDateTo(""); };
  const clearFilters = () => { setDateFrom(""); setDateTo(""); setCourtType(""); setPinakioSearch(""); };

  const togglePinakio = (label) => setExpandedPinakia(prev => ({ ...prev, [label]: !prev[label] }));
  const toggleDate = (key) => setExpandedDates(prev => ({ ...prev, [key]: !prev[key] }));

  const expandAll = () => {
    const ep = {}; const ed = {};
    displayPinakia.forEach(p => {
      ep[p.label] = true;
      p.dates.forEach(d => { ed[`${p.label}|${d.date}`] = true; });
    });
    setExpandedPinakia(ep); setExpandedDates(ed);
  };
  const collapseAll = () => { setExpandedPinakia({}); setExpandedDates({}); };

  const fetchAvailableSlots = async () => {
    try {
      setSlotsLoading(true);
      const params = new URLSearchParams();
      if (slotsDateFrom) params.append("date_from", slotsDateFrom);
      if (slotsDateTo) params.append("date_to", slotsDateTo);
      if (slotsSubject) params.append("subject", slotsSubject);
      if (slotsCourtType) params.append("court_type", slotsCourtType);
      if (slotsPinakioSearch) params.append("pinakio_search", slotsPinakioSearch);
      if (branchFilter) params.append("branch", branchFilter);
      const res = await axios.get(`${API}/hearings/available-slots?${params.toString()}`);
      setSlotsData(res.data);
    } catch {
      toast.error("Σφάλμα αναζήτησης κενών θέσεων");
    } finally {
      setSlotsLoading(false);
    }
  };

  const handleSlotsExport = async (format) => {
    try {
      setSlotsExporting(true);
      const params = new URLSearchParams();
      params.append("format", format);
      if (slotsDateFrom) params.append("date_from", slotsDateFrom);
      if (slotsDateTo) params.append("date_to", slotsDateTo);
      if (slotsSubject) params.append("subject", slotsSubject);
      if (slotsCourtType) params.append("court_type", slotsCourtType);
      if (slotsPinakioSearch) params.append("pinakio_search", slotsPinakioSearch);
      if (branchFilter) params.append("branch", branchFilter);

      // Start background job
      const startRes = await axios.post(
        `${API}/exports/jobs/empty-slots?${params.toString()}`
      );
      const jobId = startRes.data?.job_id;
      if (!jobId) throw new Error("Δεν επιστράφηκε job_id");
      setExportJob({ job_id: jobId, status: "pending", progress: 0, message: "Ξεκινά...", format });

      // Poll status
      const pollStart = Date.now();
      const POLL_TIMEOUT_MS = 10 * 60 * 1000; // 10 λεπτά max
      while (Date.now() - pollStart < POLL_TIMEOUT_MS) {
        await new Promise(r => setTimeout(r, 1500));
        let st;
        try {
          st = await axios.get(`${API}/exports/jobs/${jobId}`);
        } catch (pollErr) {
          // 404 = expired/missing - abort
          throw new Error("Το job δεν βρέθηκε στον server");
        }
        const job = st.data;
        setExportJob({ ...job, format });
        if (job.status === "done") {
          // Download
          const dlRes = await axios.get(
            `${API}/exports/jobs/${jobId}/download`,
            { responseType: "blob" }
          );
          const ext = format === "odt" ? "odt" : "pdf";
          const mime = format === "odt"
            ? "application/vnd.oasis.opendocument.text"
            : "application/pdf";
          const url = window.URL.createObjectURL(new Blob([dlRes.data], { type: mime }));
          const a = document.createElement("a");
          a.href = url;
          a.download = job.filename || `empty_pinakia_slots.${ext}`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
          toast.success(`Εξαγωγή ${ext.toUpperCase()} ολοκληρώθηκε`);
          // Auto-close dialog after 1 sec
          setTimeout(() => setExportJob(null), 1500);
          return;
        }
        if (job.status === "error") {
          toast.error(`Σφάλμα: ${job.error || "Άγνωστο"}`);
          return;
        }
      }
      // Timeout
      toast.error("Η εξαγωγή υπερβαίνει το όριο 10 λεπτών — δοκιμάστε με μικρότερο φίλτρο");
    } catch (e) {
      let detail = e?.message || "Άγνωστο σφάλμα";
      try {
        if (e?.response?.data instanceof Blob) {
          const txt = await e.response.data.text();
          try { detail = JSON.parse(txt).detail || txt; } catch { detail = txt; }
        } else if (e?.response?.data?.detail) {
          detail = e.response.data.detail;
        }
      } catch { /* ignore */ }
      console.error("Export error:", e?.response?.status, detail);
      toast.error(`Σφάλμα εξαγωγής: ${String(detail).slice(0, 200)}`);
    } finally {
      setSlotsExporting(false);
    }
  };

  const downloadHearingBulletin = async (hearing_date, court_type, fmt, pinakio_code = "") => {
    try {
      const params = new URLSearchParams({
        court_type, hearing_date, format: fmt,
      });
      if (pinakio_code) params.set("pinakio_code", pinakio_code);
      const res = await axios.get(
        `${API}/pretrial-tools/hearing-bulletin?${params.toString()}`,
        { responseType: "blob" }
      );
      const mime = fmt === "pdf"
        ? "application/pdf"
        : "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
      // Έλεγχος ότι το blob είναι όντως ο σωστός τύπος (όχι JSON error)
      const ct = res.headers?.["content-type"] || "";
      if (!ct.includes(mime.split(";")[0])) {
        const txt = await res.data.text();
        let errMsg = txt;
        try { errMsg = JSON.parse(txt).detail || txt; } catch { /* keep as text */ }
        throw new Error(String(errMsg).slice(0, 300));
      }
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Ενημερωτικό_Δελτίο_Δικασίμου_${court_type}_${hearing_date}.${fmt}`;
      // ΣΗΜΑΝΤΙΚΟ: append στο DOM ΠΡΙΝ το click — αλλιώς Firefox/Safari αποτυγχάνει σιωπηλά
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Λήψη ${fmt.toUpperCase()} ολοκληρώθηκε`);
    } catch (e) {
      let detail = e?.message || "Σφάλμα";
      try {
        if (e?.response?.data instanceof Blob) {
          const txt = await e.response.data.text();
          try { detail = JSON.parse(txt).detail || txt; } catch { detail = txt; }
        }
      } catch { /* ignore */ }
      toast.error(`Σφάλμα δελτίου: ${String(detail).slice(0, 200)}`);
    }
  };

  const handlePrint = (pinakio, dateGroup) => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) { toast.error("Ενεργοποιήστε τα αναδυόμενα παράθυρα"); return; }
    
    const displayDate = new Date(dateGroup.date + "T00:00:00").toLocaleDateString("el-GR", {
      weekday: "long", day: "numeric", month: "long", year: "numeric"
    });
    
    const rows = dateGroup.cases.map((c, i) => `
      <tr>
        <td style="text-align:center;padding:6px 8px;border:1px solid #ccc;">${c.sequence_number || (i + 1)}</td>
        <td style="padding:6px 8px;border:1px solid #ccc;font-family:monospace;font-weight:bold;">${c.gak_number}/${c.gak_year}</td>
        <td style="padding:6px 8px;border:1px solid #ccc;">${c.eak_number || "-"}</td>
        <td style="padding:6px 8px;border:1px solid #ccc;">${c.court_type || ""}</td>
        <td style="padding:6px 8px;border:1px solid #ccc;">${c.subject || "-"}</td>
        <td style="padding:6px 8px;border:1px solid #ccc;">${privacy ? '**' : (c.plaintiffs || "-")}</td>
        <td style="padding:6px 8px;border:1px solid #ccc;">${privacy ? '**' : (c.defendants || "-")}</td>
        <td style="padding:6px 8px;border:1px solid #ccc;">${c.assigned_judge || "-"}</td>
      </tr>
    `).join("");

    const assignedJudge = dateGroup.cases.find(c => c.assigned_judge)?.assigned_judge || "";
    const benchJudge = dateGroup.bench_judge || (dateGroup.bench_judges || []).join(", ");

    printWindow.document.write(`<!DOCTYPE html><html><head><title>Πινάκιο ${pinakio.label} - ${displayDate}</title>
      <style>
        body { font-family: 'Segoe UI', Arial, sans-serif; margin: 20px; color: #333; }
        h1 { font-size: 18px; margin-bottom: 4px; }
        h2 { font-size: 14px; color: #666; margin-bottom: 16px; }
        table { width: 100%; border-collapse: collapse; font-size: 12px; }
        th { background: #f1f5f9; padding: 8px; border: 1px solid #ccc; text-align: left; font-weight: 600; }
        @media print { body { margin: 10mm; } }
      </style></head><body>
      <h1>Πινάκιο: ${pinakio.label}</h1>
      <h2>${displayDate} - ${dateGroup.cases.length} υποθέσεις${assignedJudge ? ` - Δικαστής Προδικαστικού: ${assignedJudge}` : ""}${benchJudge ? ` - Δικαστής Έδρας: ${benchJudge}` : ""}</h2>
      <table>
        <thead><tr>
          <th style="width:40px;">Α/Α</th><th>ΓΑΚ</th><th>ΕΑΚ</th><th>Τύπος</th><th>Αντικείμενο</th><th>Ενάγοντες</th><th>Εναγόμενοι</th><th>Δικαστής</th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <script>window.onload=function(){window.print();}</script>
    </body></html>`);
    printWindow.document.close();
  };

  return (
    <div className="space-y-5 animate-fadeIn" data-testid="hearings-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">{isPresidentRole(user) ? "Δικάσιμοι" : isScopedRole(user) ? "Τα Πινάκια Μου" : "Πινάκια"}</h1>
        <p className="text-slate-500 mt-1">Ομαδοποίηση ανά Πινάκιο → Ημερομηνία → Υποθέσεις</p>
      </div>
      <RoleScopeBanner />

      {/* Tab Navigation */}
      <div className="flex border-b border-slate-200 gap-1">
        <button className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 ${hearingsView === "pinakia" ? "border-primary text-primary" : "border-transparent text-slate-500 hover:text-slate-700"}`}
          onClick={() => setHearingsView("pinakia")} data-testid="tab-pinakia">
          Πινάκια
        </button>
        <button className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 flex items-center gap-2 ${hearingsView === "slots" ? "border-primary text-primary" : "border-transparent text-slate-500 hover:text-slate-700"}`}
          onClick={() => setHearingsView("slots")} data-testid="tab-slots-search">
          Αναζήτηση Κενών Θέσεων
        </button>
      </div>

      {hearingsView === "pinakia" && (<>

      {/* Filters */}
      <Card>
        <CardContent className="pt-5 pb-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Από</Label>
              <DateInput value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-40" data-testid="hearing-date-from" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Έως</Label>
              <DateInput value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-40" data-testid="hearing-date-to" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Κατάστημα</Label>
              <BranchFilterSelect value={branchFilter} onChange={setBranchFilter} className="w-56" testId="hearing-branch-filter" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Τύπος</Label>
              <Select value={courtType} onValueChange={v => setCourtType(v)}
                disabled={user?.role !== "admin" && userCourtTypes.length === 1}>
                <SelectTrigger className="w-36" data-testid="hearing-court-type"><SelectValue placeholder="Όλα" /></SelectTrigger>
                <SelectContent>
                  {(user?.role === "admin" || userCourtTypes.length > 1) && (
                    <SelectItem value="all">Όλα</SelectItem>
                  )}
                  {(user?.role === "admin" ? ["Μονομελές", "Πολυμελές"] : userCourtTypes).map(ct => (
                    <SelectItem key={ct} value={ct}>{ct}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Αναζήτηση Πινακίου</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
                <Input value={pinakioSearch} onChange={e => setPinakioSearch(e.target.value)}
                  placeholder="π.χ. 158 ή ΓΚΑ" className="pl-8 w-44" data-testid="hearing-pinakio-search" />
              </div>
            </div>
            <div className="flex gap-1.5">
              <Button variant="default" size="sm" onClick={setUpcoming} data-testid="hearing-upcoming-btn">Επερχόμενα</Button>
              <Button variant="outline" size="sm" onClick={setToday} data-testid="hearing-today-btn">Σήμερα</Button>
              <Button variant="outline" size="sm" onClick={setThisWeek} data-testid="hearing-week-btn">Εβδομάδα</Button>
              <Button variant="outline" size="sm" onClick={setThisMonth} data-testid="hearing-month-btn">Μήνας</Button>
              <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="hearing-clear-btn">Όλα</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Badge variant="secondary" className="text-sm" data-testid="hearing-total-pinakia">
            {displayPinakia.length} πινάκια
          </Badge>
          <Badge variant="outline" className="text-sm" data-testid="hearing-total-cases">
            {displayCaseCount} υποθέσεις
          </Badge>
        </div>
        {displayPinakia.length > 0 && (
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={expandAll}>Ανάπτυξη όλων</Button>
            <Button variant="ghost" size="sm" onClick={collapseAll}>Σύμπτυξη όλων</Button>
          </div>
        )}
      </div>

      {/* Grouped by Pinakio → Date → Cases */}
      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-20 w-full" />)}</div>
      ) : displayPinakia.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Calendar className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-500">Δεν βρέθηκαν πινάκια {pinakioSearch && `για "${pinakioSearch}"`}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {displayPinakia.map(pinakio => (
            <Card key={pinakio.label} className="overflow-hidden" data-testid={`pinakio-${pinakio.pinakio_code}`}>
              {/* Pinakio header */}
              <button
                className="w-full flex items-center justify-between px-5 py-3.5 bg-slate-800 hover:bg-slate-700 transition-colors text-left"
                onClick={() => togglePinakio(pinakio.label)}
                data-testid={`pinakio-toggle-${pinakio.pinakio_code}`}
              >
                <div className="flex items-center gap-3">
                  {expandedPinakia[pinakio.label] ?
                    <ChevronDown className="w-5 h-5 text-slate-300" /> :
                    <ChevronRight className="w-5 h-5 text-slate-300" />
                  }
                  <span className="font-bold text-white text-base">{pinakio.label}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant="outline" className="border-slate-500 text-slate-300 text-xs">
                    {pinakio.dates.length} {pinakio.dates.length === 1 ? "ημερομηνία" : "ημερομηνίες"}
                  </Badge>
                  <Badge className="bg-white/15 text-white border-0 text-xs">
                    {pinakio.total_cases} υποθ.
                  </Badge>
                </div>
              </button>

              {/* Dates within pinakio */}
              {expandedPinakia[pinakio.label] && (
                <div>
                  {pinakio.dates.map(dateGroup => {
                    const dateKey = `${pinakio.label}|${dateGroup.date}`;
                    const past = isDatePast(dateGroup.date);
                    return (
                      <div key={dateGroup.date}>
                        {/* Date sub-header */}
                        <div className={`flex items-center justify-between px-5 py-2.5 border-b transition-colors ${past ? "bg-slate-100" : "bg-blue-50"}`}>
                          <button
                            className="flex items-center gap-2.5 flex-1 text-left"
                            onClick={() => toggleDate(dateKey)}
                            data-testid={`date-toggle-${pinakio.pinakio_code}-${dateGroup.date}`}
                          >
                            {expandedDates[dateKey] ?
                              <ChevronDown className="w-4 h-4 text-slate-400" /> :
                              <ChevronRight className="w-4 h-4 text-slate-400" />
                            }
                            <Calendar className={`w-4 h-4 ${past ? "text-slate-400" : "text-blue-600"}`} />
                            <span className={`font-semibold text-sm ${past ? "text-slate-500" : "text-slate-800"}`}>
                              {formatDateDisplay(dateGroup.date)}
                            </span>
                            <Badge variant={past ? "secondary" : "default"} className="text-xs">
                              {dateGroup.case_count} {dateGroup.case_count === 1 ? "υπόθεση" : "υποθέσεις"}
                            </Badge>
                            {dateGroup.needs_reassignment_count > 0 && (
                              <span className="inline-flex items-center rounded bg-red-600 text-white text-[10px] font-extrabold px-1.5 py-0.5 tracking-wide"
                                    title="Υποθέσεις όπου ΑΠΑΙΤΕΙΤΑΙ ΑΝΑΧΡΕΩΣΗ"
                                    data-testid={`needs-reassignment-count-${pinakio.pinakio_code}-${dateGroup.date}`}>
                                {dateGroup.needs_reassignment_count} ΑΠΑΙΤΕΙΤΑΙ ΑΝΑΧΡΕΩΣΗ
                              </span>
                            )}
                          </button>
                          <div className="flex items-center gap-1">
                          {assignJudgeFor?.pinakioLabel === pinakio.label && assignJudgeFor?.date === dateGroup.date ? (
                            <div className="flex flex-col gap-1 relative">
                              <div className="flex items-center gap-1">
                                {(() => {
                                  const ct = dateGroup?.cases?.[0]?.court_type || "Μονομελές";
                                  const judges = pretrialJudgesByCT[ct] || [];
                                  return (
                                    <Select value={judgeName} onValueChange={setJudgeName}>
                                      <SelectTrigger
                                        className="h-7 w-56 text-xs"
                                        data-testid={`judge-select-${pinakio.pinakio_code}-${dateGroup.date}`}
                                      >
                                        <SelectValue placeholder={judges.length ? `Επιλέξτε Δικαστή (${ct})` : "Δεν υπάρχουν Δικαστές Προδικαστικού"} />
                                      </SelectTrigger>
                                      <SelectContent className="max-h-80">
                                        {judges.length === 0 ? (
                                          <div className="px-3 py-2 text-xs text-slate-500">
                                            Καταχωρήστε δικαστές στο /pretrial → Δικαστές Προδικαστικού
                                          </div>
                                        ) : judges.map(j => (
                                          <SelectItem key={j.id} value={j.full_name}>
                                            <span className="flex items-center gap-1">
                                              <UserCheck className="w-3 h-3 text-indigo-500" />
                                              <JudgeOptionLabel judge={j} />
                                            </span>
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  );
                                })()}
                                <Button
                                  variant="default"
                                  size="sm"
                                  className="h-7 text-xs"
                                  disabled={assigning || !judgeName}
                                  onClick={() => handleAssignJudge(pinakio.label, dateGroup.date)}
                                  data-testid={`assign-judge-confirm-${pinakio.pinakio_code}-${dateGroup.date}`}
                                >
                                  {assigning ? <Loader2 className="w-3 h-3 animate-spin" /> : "OK"}
                                </Button>
                                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { setAssignJudgeFor(null); setJudgeName(""); setJudgeSearch(""); }}>
                                  &#x2715;
                                </Button>
                              </div>
                              {judgeName && <p className="text-xs text-emerald-600 ml-1">Επιλεγμένος: {judgeName}</p>}
                            </div>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 text-xs gap-1 text-indigo-600 hover:text-indigo-800"
                              onClick={() => setAssignJudgeFor({ pinakioLabel: pinakio.label, date: dateGroup.date })}
                              data-testid={`assign-judge-${pinakio.pinakio_code}-${dateGroup.date}`}
                            >
                              <UserCheck className="w-3.5 h-3.5" />
                              Ορισμός Δικαστή
                            </Button>
                          )}
                          <BenchJudgePicker
                            pinakio={pinakio}
                            dateGroup={dateGroup}
                            judges={pretrialJudgesByCT[dateGroup?.cases?.[0]?.court_type || "Μονομελές"] || []}
                            onDone={fetchHearings}
                          />
                          {((dateGroup?.cases?.[0]?.court_type === "Πολυμελές") || /\(ΠΟΛ\)/i.test(pinakio.label || "") || dateGroup.composition) && (
                            <>
                              <CompositionPicker pinakio={pinakio} dateGroup={dateGroup} onDone={fetchHearings} />
                              {dateGroup.composition && (
                                <SynthesisActsButton pinakioLabel={pinakio.label} hearingDate={dateGroup.date} compositionLabel={dateGroup.composition?.label}
                                                     onDone={fetchHearings} testId={`synthesis-acts-${pinakio.pinakio_code}-${dateGroup.date}`} />
                              )}
                            </>
                          )}
                          {(dateGroup.pretrial_judges || []).length > 0 && (
                            <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 text-orange-700 hover:text-orange-900"
                                    onClick={() => setReassignTarget({ pinakioLabel: pinakio.label, hearingDate: dateGroup.date, courtType: dateGroup?.cases?.[0]?.court_type || pinakio.court_type || undefined })}
                                    title="ΑΝΑΧΡΕΩΣΗ: αντικατάσταση δικαστή (προδικαστικού + έδρας) + Πράξεις Νέου Ορισμού (ZIP)"
                                    data-testid={`reassign-${pinakio.pinakio_code}-${dateGroup.date}`}>
                              <UserCog className="w-3.5 h-3.5" />ΑΝΑΧΡΕΩΣΗ
                            </Button>
                          )}
                          <DocketButton pinakioName={pinakio.tmima || pinakio.label} pinakioCode={pinakio.pinakio_code} date={dateGroup.date}
                                        entry={docketMap[docketKey(pinakio.tmima || pinakio.label, dateGroup.date)]}
                                        onFetched={(d) => setDocketMap(m => ({ ...m, [docketKey(d.pinakio_name, d.hearing_date)]: { id: d.id, line_count: d.line_count, matched_count: (d.matched_case_ids || []).length, fetched_at: d.fetched_at } }))} />
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs gap-1 text-slate-500 hover:text-slate-800"
                            onClick={() => handlePrint(pinakio, dateGroup)}
                            data-testid={`print-${pinakio.pinakio_code}-${dateGroup.date}`}
                          >
                            <Printer className="w-3.5 h-3.5" />
                            Εκτύπωση
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs gap-1 text-rose-600 hover:text-rose-800"
                            onClick={() => downloadHearingBulletin(dateGroup.date, pinakio.court_type || (dateGroup.cases?.[0]?.court_type) || "Μονομελές", "pdf", pinakio.pinakio_code || "")}
                            data-testid={`bulletin-pdf-${pinakio.pinakio_code}-${dateGroup.date}`}
                            title="Ενημερωτικό Δελτίο Δικασίμου (PDF)"
                          >
                            <FileText className="w-3.5 h-3.5" />
                            Δελτίο PDF
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs gap-1 text-blue-700 hover:text-blue-900"
                            onClick={() => downloadHearingBulletin(dateGroup.date, pinakio.court_type || (dateGroup.cases?.[0]?.court_type) || "Μονομελές", "docx", pinakio.pinakio_code || "")}
                            data-testid={`bulletin-docx-${pinakio.pinakio_code}-${dateGroup.date}`}
                            title="Ενημερωτικό Δελτίο Δικασίμου (Word)"
                          >
                            <FileText className="w-3.5 h-3.5" />
                            Δελτίο Word
                          </Button>
                          </div>
                        </div>

                        {/* Cases */}
                        {expandedDates[dateKey] && (
                          <div className="divide-y divide-slate-100">
                            <div className="grid grid-cols-[45px_1fr_90px_180px_180px_120px_120px] gap-2 px-5 py-1.5 bg-slate-50/70 text-[11px] text-slate-400 font-medium uppercase tracking-wider">
                              <span>Α/Α</span>
                              <span>ΓΑΚ / Αντικείμενο</span>
                              <span>ΕΑΚ</span>
                              <span>Ενάγοντες</span>
                              <span>Εναγόμενοι</span>
                              <span>Δικαστής Προδ.</span>
                              <span>Δικαστής Έδρας</span>
                            </div>
                            {dateGroup.cases.map((c, i) => (
                              <div key={c.id || i}
                                className="grid grid-cols-[45px_1fr_90px_180px_180px_120px_120px] gap-2 items-center px-5 py-2 hover:bg-slate-50 transition-colors"
                                data-testid={`hearing-case-${c.id}`}
                              >
                                <span className="text-xs text-slate-400 font-mono text-center">{c.sequence_number || "-"}</span>
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2">
                                    <Link to={`/cases/${c.id}`} className="font-mono font-semibold text-primary hover:underline text-sm" data-testid={`hearing-link-${c.id}`}>
                                      {c.gak_number}/{c.gak_year}
                                    </Link>
                                    <Badge variant="outline" className="text-[10px] px-1.5 py-0">{c.court_type}</Badge>
                                  </div>
                                  {c.subject && <span className="text-xs text-slate-500">{c.subject}</span>}
                                </div>
                                <span className="text-xs text-slate-600 font-mono">{c.eak_number || "-"}</span>
                                <span className="text-xs text-slate-600 truncate">{p(c.plaintiffs) || "-"}</span>
                                <span className="text-xs text-slate-500 truncate">{p(c.defendants) || "-"}</span>
                                <span className="text-xs text-indigo-600 truncate">{c.assigned_judge || "-"}</span>
                                <div className="min-w-0 flex flex-col gap-0.5">
                                  <span className="text-xs text-amber-700 truncate" data-testid={`hearing-bench-${c.id}`}>{c.bench_judge || "-"}</span>
                                  <NeedsReassignmentBadge need={c.needs_reassignment} caseId={c.id} compact
                                    onClick={user?.role === "judge" ? undefined : () => setReassignTarget(singleCaseReassignTarget({
                                      pinakioLabel: pinakio.label, hearingDate: dateGroup.date, courtType: c.court_type || pinakio.court_type || undefined,
                                      caseId: c.id, benchJudge: c.bench_judge }))} />
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      </>)}

      {/* Αναζήτηση Κενών Θέσεων */}
      {hearingsView === "slots" && (
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Αναζήτηση Διαθέσιμων/Κενών Θέσεων Πινακίων</CardTitle>
              <CardDescription>Κανόνας: Κάθε πινάκιο πληρούται με 15 υποθέσεις</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Ημ/νία Από</Label>
                  <DateInput value={slotsDateFrom} onChange={e => setSlotsDateFrom(e.target.value)} data-testid="slots-date-from" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Ημ/νία Έως</Label>
                  <DateInput value={slotsDateTo} onChange={e => setSlotsDateTo(e.target.value)} data-testid="slots-date-to" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Πινάκιο</Label>
                  <Input placeholder="π.χ. 158-ΚΑΛ" value={slotsPinakioSearch} onChange={e => setSlotsPinakioSearch(e.target.value)} data-testid="slots-pinakio-search" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Αντικείμενο</Label>
                  <Select
                    value={slotsSubject || "all"}
                    onValueChange={(v) => setSlotsSubject(v === "all" ? "" : v)}
                  >
                    <SelectTrigger data-testid="slots-subject">
                      <SelectValue placeholder="Όλα τα αντικείμενα" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      <SelectItem value="all">Όλα τα αντικείμενα</SelectItem>
                      {subjectOptions.map((s) => {
                        const val = s.subject || s;
                        const cnt = s.count;
                        return (
                          <SelectItem key={val} value={val}>
                            {val}{cnt != null ? ` (${cnt})` : ""}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Κατάστημα</Label>
                  <BranchFilterSelect value={branchFilter} onChange={setBranchFilter} className="w-56" testId="slots-branch-filter" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Τύπος</Label>
                  <Select value={slotsCourtType || "all"} onValueChange={v => setSlotsCourtType(v === "all" ? "" : v)}>
                    <SelectTrigger data-testid="slots-court-type"><SelectValue placeholder="Όλα" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Όλα</SelectItem>
                      {(courtTypeOptions.length > 0 ? courtTypeOptions : ["Μονομελές", "Πολυμελές"]).map(ct => (
                        <SelectItem key={ct} value={ct}>{ct}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-4">
                <Button onClick={fetchAvailableSlots} disabled={slotsLoading} data-testid="slots-search-btn">
                  {slotsLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                  Αναζήτηση Κενών Θέσεων
                </Button>
                {slotsData && (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => handleSlotsExport("pdf")}
                      disabled={slotsExporting}
                      data-testid="slots-export-pdf-btn"
                    >
                      {slotsExporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Printer className="w-4 h-4 mr-2" />}
                      Εξαγωγή PDF
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleSlotsExport("odt")}
                      disabled={slotsExporting}
                      data-testid="slots-export-odt-btn"
                    >
                      LibreOffice (ODT)
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {slotsData && (
            <>
              {/* Σύνοψη */}
              <div className="grid grid-cols-3 gap-4">
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="py-4 text-center">
                    <div className="text-2xl font-bold text-green-700">{slotsData.total_available_slots}</div>
                    <div className="text-xs text-green-600">Συνολικές Κενές Θέσεις</div>
                  </CardContent>
                </Card>
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="py-4 text-center">
                    <div className="text-2xl font-bold text-blue-700">{slotsData.total_boards_available}</div>
                    <div className="text-xs text-blue-600">Πινάκια με Θέσεις</div>
                  </CardContent>
                </Card>
                <Card className="bg-red-50 border-red-200">
                  <CardContent className="py-4 text-center">
                    <div className="text-2xl font-bold text-red-700">{slotsData.total_boards_exceeded}</div>
                    <div className="text-xs text-red-600">Πινάκια σε Υπέρβαση</div>
                  </CardContent>
                </Card>
              </div>

              {/* Διαθέσιμες Θέσεις */}
              {slotsData.available.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base text-green-700">Πινάκια με Κενές Θέσεις</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-green-50 text-green-800">
                          <th className="p-2 text-left">Ημερομηνία</th>
                          <th className="p-2 text-left">Πινάκιο</th>
                          <th className="p-2 text-center">Τύπος</th>
                          <th className="p-2 text-left">Αντικείμενο</th>
                          <th className="p-2 text-center">Υπάρχουσες</th>
                          <th className="p-2 text-center">Κενές Θέσεις</th>
                        </tr>
                      </thead>
                      <tbody>
                        {slotsData.available.map((b, i) => (
                          <tr key={`${b.pinakio_label || 'p'}-${b.hearing_date_fmt || i}-${b.court_type}`} className={i % 2 === 0 ? "" : "bg-slate-50"}>
                            <td className="p-2 font-medium">{b.hearing_date_fmt}</td>
                            <td className="p-2 font-semibold text-slate-800">{b.pinakio_label || "-"}</td>
                            <td className="p-2 text-center">{b.court_type}</td>
                            <td className="p-2">{b.subject || "-"}</td>
                            <td className="p-2 text-center">{b.count}/{b.max_slots}</td>
                            <td className="p-2 text-center">
                              <Badge className="bg-green-100 text-green-800">{b.available_slots}</Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              )}

              {/* Πινάκια σε Υπέρβαση */}
              {slotsData.exceeded.length > 0 && (
                <Card className="border-red-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base text-red-700">Πινάκια σε Υπέρβαση ({">"}15 υποθέσεις)</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    {slotsData.exceeded.map((b, i) => (
                      <div key={`${b.pinakio_label || 'p'}-${b.hearing_date_fmt || i}`} className="border-b last:border-b-0">
                        <div className="flex items-center justify-between p-3 bg-red-50">
                          <div>
                            <span className="font-bold text-slate-900">{b.pinakio_label || "—"}</span>
                            <span className="mx-2 text-slate-400">|</span>
                            <span className="font-semibold">{b.hearing_date_fmt}</span>
                            <span className="mx-2 text-slate-400">|</span>
                            <span>{b.subject || "-"}</span>
                            <span className="mx-2 text-slate-400">|</span>
                            <span className="text-sm">{b.court_type}</span>
                          </div>
                          <Badge variant="destructive">{b.count} υποθ. (+{b.exceeded_by})</Badge>
                        </div>
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="bg-slate-100">
                              <th className="p-1.5 text-left">ΓΑΚ</th>
                              <th className="p-1.5 text-left">ΕΑΚ</th>
                              <th className="p-1.5 text-left">Ενάγοντες</th>
                              <th className="p-1.5 text-left">Εναγόμενοι</th>
                              <th className="p-1.5 text-left">Δικαστής</th>
                            </tr>
                          </thead>
                          <tbody>
                            {b.cases.map((c, j) => (
                              <tr key={c.id || `${c.gak}-${c.eak}-${j}`} className={j % 2 === 0 ? "" : "bg-slate-50"}>
                                <td className="p-1.5 font-mono">{c.gak}</td>
                                <td className="p-1.5">{c.eak}</td>
                                <td className="p-1.5">{c.plaintiffs}</td>
                                <td className="p-1.5">{c.defendants}</td>
                                <td className="p-1.5">{c.assigned_judge || "-"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {slotsData.available.length === 0 && slotsData.exceeded.length === 0 && (
                <Card>
                  <CardContent className="py-12 text-center text-slate-400">
                    <Search className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Δεν βρέθηκαν πινάκια με τα κριτήρια αναζήτησης</p>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      )}

      {/* Email Confirmation Dialog - ρωτάει αν θέλει αποστολή */}
      {showEmailConfirm && !emailDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowEmailConfirm(null)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Mail className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900">Αποστολή Email;</h3>
                <p className="text-sm text-slate-500">Θέλετε να στείλετε ενημερωτικό email στον δικαστή με το πινάκιο και τις υποθέσεις;</p>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" onClick={() => setShowEmailConfirm(null)} data-testid="email-cancel-btn">
                Όχι, ευχαριστώ
              </Button>
              <Button onClick={() => { setEmailDialog(showEmailConfirm); setShowEmailConfirm(null); }} data-testid="email-yes-btn">
                <Mail className="w-4 h-4 mr-1" /> Ναι, προβολή Email
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Email Content Dialog */}
      {emailDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setEmailDialog(null)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b bg-slate-50 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-slate-900 flex items-center gap-2">
                  <Mail className="w-5 h-5 text-blue-600" /> Email Ορισμού Δικαστή
                </h3>
                <p className="text-xs text-slate-500 mt-1">{emailDialog.subject}</p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={handleCopyEmail} data-testid="copy-email-btn">
                  <Copy className="w-3 h-3 mr-1" /> Αντιγραφή
                </Button>
                <Button size="sm" onClick={handleSendEmail} data-testid="send-email-btn">
                  <Mail className="w-3 h-3 mr-1" /> Αποστολή
                </Button>
              </div>
            </div>
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              <pre className="text-sm text-slate-700 whitespace-pre-wrap font-sans">{emailDialog.body}</pre>
            </div>
            <div className="p-3 border-t flex justify-end">
              <Button variant="ghost" onClick={() => setEmailDialog(null)}>Κλείσιμο</Button>
            </div>
          </div>
        </div>
      )}

      {/* Export Job Progress Dialog */}
      <Dialog
        open={!!exportJob}
        onOpenChange={(open) => {
          if (!open && exportJob?.status === "done") setExportJob(null);
        }}
      >
        <DialogContent className="sm:max-w-md" data-testid="export-job-dialog">
          <DialogHeader>
            <DialogTitle>
              {exportJob?.status === "done"
                ? "✅ Η εξαγωγή ολοκληρώθηκε"
                : exportJob?.status === "error"
                ? "❌ Σφάλμα εξαγωγής"
                : `Δημιουργία ${(exportJob?.format || "PDF").toUpperCase()}...`}
            </DialogTitle>
            <DialogDescription>
              {exportJob?.message || "Σε αναμονή..."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden">
              <div
                className={`h-3 transition-all duration-500 ${
                  exportJob?.status === "error"
                    ? "bg-red-500"
                    : exportJob?.status === "done"
                    ? "bg-green-500"
                    : "bg-primary"
                }`}
                style={{ width: `${exportJob?.progress || 0}%` }}
                data-testid="export-progress-bar"
              />
            </div>
            <div className="text-xs text-slate-500 flex justify-between">
              <span>{exportJob?.progress || 0}%</span>
              <span>{exportJob?.status}</span>
            </div>
            {exportJob?.error && (
              <div className="text-sm text-red-600 bg-red-50 p-2 rounded border border-red-200">
                {exportJob.error}
              </div>
            )}
            {exportJob?.status !== "done" && exportJob?.status !== "error" && (
              <div className="text-xs text-slate-500 italic">
                💡 Μπορείτε να συνεχίσετε να χρησιμοποιείτε την εφαρμογή — η εξαγωγή τρέχει στο background.
              </div>
            )}
          </div>
          <DialogFooter>
            {(exportJob?.status === "done" || exportJob?.status === "error") && (
              <Button onClick={() => setExportJob(null)} data-testid="close-export-dialog">
                Κλείσιμο
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ReassignDialog target={reassignTarget} onClose={() => setReassignTarget(null)} onDone={fetchHearings} />
    </div>
  );
};

export default Hearings;
